import { useMemo, useState } from "react";
import { PencilIcon, PlusIcon, ChevronRightIcon, ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useLibraryBrowser } from "./LibraryBrowserContext";
import type { LibraryItem, LibrarySection, LibraryGroup } from "./LibraryBrowserContext";
import TagLibraryResults from "../TagLibraryResults";
import { useTagLibrarySearch } from "@/shared/hooks";

// ── Types ───────────────────────────────────────────────────────────────

interface DisplayGroup {
  sectionId: string;
  groupLabel: string;
  categories: { label: string; items: LibraryItem[] }[];
  items: LibraryItem[];
  createAction?: { label: string; onClick: () => void };
}

// ── Component ───────────────────────────────────────────────────────────

export default function ContentArea() {
  const {
    activePath,
    sections,
    searchQuery,
    mode,
    selections,
    onToggle,
    selectedItem,
    selectItem,
    onItemEdit,
    navigateTo,
  } = useLibraryBrowser();

  // ── Root level: show active selections or empty state ────────────────
  if (activePath.length === 0 && !searchQuery.trim()) {
    // Collect active items across all sections
    const activeItems: { item: LibraryItem; sectionId: string; sectionLabel: string; selectionKey: string }[] = [];
    for (const [key, ids] of Object.entries(selections)) {
      if (!ids || ids.length === 0) continue;
      for (const id of ids) {
        for (const section of sections) {
          const sectionKey = section.id.includes("--") ? section.id.split("--")[0] : section.id;
          if (sectionKey !== key) continue;
          for (const group of section.groups) {
            const found = group.items.find((i) => i.id === id);
            if (found) {
              activeItems.push({ item: found, sectionId: section.id, sectionLabel: section.label, selectionKey: key });
            }
          }
        }
      }
    }

    if (activeItems.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center px-6">
          <p className="text-sm text-gray-500 text-center">
            {mode === "builder"
              ? "Pick a watchlist from the sidebar to add its entries to this one"
              : "Select a section from the sidebar to browse"
            }
          </p>
        </div>
      );
    }

    return (
      <ActiveItemsView
        activeItems={activeItems}
        selectedItem={selectedItem}
        selectItem={selectItem}
        onToggle={onToggle}
      />
    );
  }

  // ── Section level: show groups as navigable rows ──────────────────────
  if (activePath.length === 1 && !searchQuery.trim()) {
    const section = sections.find((s) => s.id === activePath[0]);
    if (!section) return null;

    // Find the create action from any group in this section
    const sectionCreateAction = section.groups.find((g) => g.createAction)?.createAction;

    // If only one group, skip group view and go straight to items
    if (section.groups.length === 1) {
      return (
        <ItemListView
          section={section}
          group={section.groups[0]}
          mode={mode}
          selections={selections}
          onToggle={onToggle}
          selectedItem={selectedItem}
          selectItem={selectItem}
          onItemEdit={onItemEdit}
        />
      );
    }

    return (
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {/* Create button at top */}
        {sectionCreateAction && (
          <SectionCreateButton action={sectionCreateAction} />
        )}

        <div className="py-2">
          {section.groups.map((group) => (
            <button
              key={group.id}
              onClick={() => navigateTo([section.id, group.id])}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-800/50 transition-colors border-b border-gray-700/20 focus:outline-none"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-200">
                  {group.label}
                </div>
                {group.items.length > 0 && (
                  <div className="text-2xs text-gray-500 mt-0.5 truncate">
                    {group.items.slice(0, 4).map((i) => i.label).join(", ")}
                    {group.items.length > 4 && `, +${group.items.length - 4} more`}
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0">
                {group.items.length}
              </span>
              <ChevronRightIcon className="size-4 text-gray-600 flex-shrink-0" />
            </button>
          ))}
        </div>

      </div>
    );
  }

  // ── Group/category level or search: show items ────────────────────────
  return <ItemListFromPath />;
}

// ── Item list resolved from activePath ──────────────────────────────────

function ItemListFromPath() {
  const {
    activePath,
    sections,
    searchQuery,
    mode,
    selections,
    onToggle,
    selectedItem,
    selectItem,
    onItemEdit,
    tagLibrary,
  } = useLibraryBrowser();

  const displayGroups = useMemo(
    () => resolveDisplayGroups(activePath, sections, searchQuery),
    [activePath, sections, searchQuery]
  );

  // Opt-in, and inert until someone types: the sections only ever hold what the
  // user owns plus the curated tags, which is a sliver of the taxonomy.
  const libraryTags = useTagLibrarySearch(tagLibrary ? searchQuery : "");
  const showLibrary = !!tagLibrary && !!searchQuery.trim();

  /** Ids the local search already surfaced, so nothing is listed twice. */
  const shownIds = useMemo(
    () =>
      new Set(
        displayGroups.flatMap((g) => [
          ...g.items.map((i) => i.id),
          ...g.categories.flatMap((c) => c.items.map((i) => i.id)),
        ])
      ),
    [displayGroups]
  );

  const isEmpty =
    !showLibrary &&
    displayGroups.every(
      (g) => g.items.length === 0 && g.categories.every((c) => c.items.length === 0)
    );

  // Find create action from any group
  const createAction = displayGroups.find((g) => g.createAction)?.createAction;

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
      {/* Create button at top */}
      {createAction && <SectionCreateButton action={createAction} />}

      {showLibrary && tagLibrary && (
        <TagLibraryResults
          tags={libraryTags.tags}
          isLoading={libraryTags.isLoading}
          isEmpty={libraryTags.isEmpty}
          query={searchQuery.trim()}
          selectedIds={selections[tagLibrary.sectionId] || []}
          onToggle={(tagId) => onToggle(tagLibrary.sectionId, tagId)}
          hiddenIds={shownIds}
        />
      )}

      {isEmpty ? (
        <div className="flex items-center justify-center h-full">
          <p className="text-xs text-gray-500">
            {searchQuery
              ? `No results for "${searchQuery}"`
              : "No items in this section"}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-700/30">
          {displayGroups.map((group) => {
            const totalItems =
              group.items.length +
              group.categories.reduce((s, c) => s + c.items.length, 0);
            if (totalItems === 0) return null;

            return (
              <div key={`${group.sectionId}-${group.groupLabel}`} className="py-2">
                {/* Group header */}
                {displayGroups.length > 1 && (
                  <div className="flex items-center justify-between px-4 py-1.5">
                    <h3 className="text-2xs font-semibold uppercase tracking-wider text-gray-500">
                      {group.groupLabel}
                    </h3>
                  </div>
                )}

                {/* Uncategorized items */}
                {group.items.length > 0 && (
                  <ItemList
                    items={group.items}
                    sectionId={group.sectionId}
                    mode={mode}
                    selections={selections}
                    onToggle={onToggle}
                    selectedItem={selectedItem}
                    selectItem={selectItem}
                    onItemEdit={onItemEdit}
                  />
                )}

                {/* Categorized items */}
                {group.categories.map((cat) => (
                  <div key={cat.label}>
                    <div className="px-4 pt-3 pb-1">
                      <h4 className="text-2xs font-semibold uppercase tracking-wider text-gray-600">
                        {cat.label}
                      </h4>
                    </div>
                    <ItemList
                      items={cat.items}
                      sectionId={group.sectionId}
                      mode={mode}
                      selections={selections}
                      onToggle={onToggle}
                      selectedItem={selectedItem}
                      selectItem={selectItem}
                      onItemEdit={onItemEdit}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

// ── Item list view for single-group sections ────────────────────────────

function ItemListView({
  section,
  group,
  mode,
  selections,
  onToggle,
  selectedItem,
  selectItem,
  onItemEdit,
}: {
  section: LibrarySection;
  group: LibraryGroup;
  mode: "filter" | "browse" | "builder";
  selections: Record<string, string[]>;
  onToggle: (menuId: string, filterId: string) => void;
  selectedItem: LibraryItem | null;
  selectItem: (item: LibraryItem | null, sectionId?: string) => void;
  onItemEdit?: (item: LibraryItem, sectionId: string) => void;
}) {
  const display = groupToDisplay(section.id, group);

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
      {/* Create button at top */}
      {group.createAction && (
        <SectionCreateButton action={group.createAction} />
      )}

      {display.items.length > 0 && (
        <ItemList
          items={display.items}
          sectionId={section.id}
          mode={mode}
          selections={selections}
          onToggle={onToggle}
          selectedItem={selectedItem}
          selectItem={selectItem}
          onItemEdit={onItemEdit}
        />
      )}

      {display.categories.map((cat) => (
        <div key={cat.label}>
          <div className="px-4 pt-3 pb-1">
            <h4 className="text-2xs font-semibold uppercase tracking-wider text-gray-600">
              {cat.label}
            </h4>
          </div>
          <ItemList
            items={cat.items}
            sectionId={section.id}
            mode={mode}
            selections={selections}
            onToggle={onToggle}
            selectedItem={selectedItem}
            selectItem={selectItem}
            onItemEdit={onItemEdit}
          />
        </div>
      ))}

    </div>
  );
}

// ── Item List ───────────────────────────────────────────────────────────

interface ItemListProps {
  items: LibraryItem[];
  sectionId: string;
  mode: "filter" | "browse" | "builder";
  selections: Record<string, string[]>;
  onToggle: (menuId: string, filterId: string) => void;
  selectedItem: LibraryItem | null;
  selectItem: (item: LibraryItem | null, sectionId?: string) => void;
  onItemEdit?: (item: LibraryItem, sectionId: string) => void;
}

function ItemList({
  items,
  sectionId,
  mode,
  selections,
  onToggle,
  selectedItem,
  selectItem,
  onItemEdit,
}: ItemListProps) {
  const { variant } = useLibraryBrowser();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  return (
    <div>
      {items.map((item) => {
        const isSelected = selectedItem?.id === item.id;
        // Map flattened section IDs to selection key (e.g., "config--sub" → "config")
        const selectionKey = sectionId.includes("--") ? sectionId.split("--")[0] : sectionId;
        const isToggled = selections[selectionKey]?.includes(item.id) ?? false;
        const isExpanded = expandedItems.has(item.id);

        // Get expandable content from metadata
        const meta = item.metadata ?? {};
        const tickers = (meta.tickers as string[]) ?? [];
        const keywords = (meta.keywords as string[]) ?? [];
        const hasExpandableContent = mode === "builder" && (tickers.length > 0 || keywords.length > 0);

        return (
          <div key={item.id}>
            <div
              className={clsx(
                "group flex items-center gap-2 px-4 py-1.5 cursor-pointer transition-all duration-100",
                isSelected
                  ? "bg-squawk-blue/10 border-l-2 border-squawk-blue"
                  : isToggled
                    ? "border-l-2 border-green-500/60 bg-green-900/5"
                    : "border-l-2 border-transparent hover:bg-gray-800/50",
                item.disabled && "opacity-40 pointer-events-none"
              )}
              onClick={() => {
                if (mode === "builder") {
                  onToggle(sectionId, item.id);
                } else {
                  selectItem(item, sectionId);
                }
              }}
            >
              {/* Checkbox (filter + builder modes) */}
              {(mode === "filter" || mode === "builder") && (
                <input
                  type="checkbox"
                  checked={isToggled}
                  onChange={() => onToggle(sectionId, item.id)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={item.disabled}
                  className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0 flex-shrink-0 cursor-pointer"
                />
              )}

              {/* Item label */}
              <span
                className={clsx(
                  "flex-1 text-xs truncate transition-colors",
                  isSelected ? "text-gray-100 font-medium" : "text-gray-300"
                )}
              >
                {item.label}
              </span>
              {meta.itemType === "advanced-filter" && (
                <span
                  title="Several watchlists joined with AND/OR rules"
                  className="shrink-0 rounded border border-gray-600 px-1 py-px text-2xs font-medium uppercase tracking-wide text-gray-400"
                >
                  Combined
                </span>
              )}

              {/* Expand toggle (builder mode — replaces color dot) */}
              {hasExpandableContent && (
                <button
                  onClick={(e) => toggleExpanded(item.id, e)}
                  className="p-0.5 text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0"
                  title={isExpanded ? "Collapse" : "View contents"}
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="size-3.5" />
                  ) : (
                    <ChevronRightIcon className="size-3.5" />
                  )}
                </button>
              )}

              {/* Color indicator — only in highlight variant or browse mode */}
              {mode !== "builder" && variant !== "filter" && item.color && (
                <span
                  className="size-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
              )}

              {/* Edit button (browse mode, on hover) */}
              {mode === "browse" && onItemEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onItemEdit(item, sectionId);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-500 hover:text-gray-300 transition-all"
                >
                  <PencilIcon className="size-3.5" />
                </button>
              )}
            </div>

            {/* Expanded content preview */}
            {isExpanded && hasExpandableContent && (
              <div className="pl-10 pr-4 py-1.5 bg-gray-900/30 border-l-2 border-gray-700/30">
                <div className="flex flex-wrap gap-1">
                  {tickers.map((t, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded text-2xs bg-blue-900/20 text-blue-300/70 border border-blue-800/30">
                      {t}
                    </span>
                  ))}
                  {keywords.map((k, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded text-2xs bg-purple-900/20 text-purple-300/70 border border-purple-800/30">
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Active Items View (expandable) ──────────────────────────────────────

function ActiveItemsView({
  activeItems,
  selectedItem,
  selectItem,
  onToggle,
}: {
  activeItems: { item: LibraryItem; sectionId: string; sectionLabel: string; selectionKey: string }[];
  selectedItem: LibraryItem | null;
  selectItem: (item: LibraryItem | null, sectionId?: string) => void;
  onToggle: (menuId: string, filterId: string) => void;
}) {
  const [collapsedActive, setCollapsedActive] = useState<Set<string>>(new Set());


  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
      <div className="px-4 py-3">
        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
          Active ({activeItems.length})
        </h3>
        <div className="space-y-1">
          {activeItems.map(({ item: activeItem, sectionId, sectionLabel, selectionKey }) => {
            const isSelected = selectedItem?.id === activeItem.id;
            /**
             * Open by default here, unlike the browse list.
             *
             * These are the lists being combined right now, and what they hold
             * is the thing being decided about — closed, the panel showed four
             * names and no way to tell whether they were what you meant
             * without clicking each one.
             */
            const isExpanded = !collapsedActive.has(activeItem.id);
            const meta = activeItem.metadata ?? {};
            const tickers = (meta.tickers as string[]) ?? [];
            const keywords = (meta.keywords as string[]) ?? [];
            const hasContent = tickers.length > 0 || keywords.length > 0;
            /**
             * A single Newsquawk tag, not a list of terms — the tag-watchlist
             * section carries both, distinguishable only by the "tag-" prefix.
             * It has no contents to expand, so labelling it as a watchlist
             * made it look like one that had failed to load.
             */
            const isSingleTag =
              meta.itemType === "tag" && !activeItem.id.startsWith("tag-");

            return (
              <div key={`${selectionKey}-${activeItem.id}`}>
                <div
                  onClick={() => selectItem(activeItem, sectionId)}
                  className={clsx(
                    "flex items-center gap-2 px-3 py-2 rounded cursor-pointer transition-all duration-100",
                    isSelected
                      ? "bg-squawk-blue/10 border-l-2 border-squawk-blue"
                      : "bg-green-900/5 border-l-2 border-green-500/60 hover:bg-gray-800/50"
                  )}
                >
                  {hasContent && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCollapsedActive((prev) => {
                          const next = new Set(prev);
                          if (next.has(activeItem.id)) next.delete(activeItem.id);
                          else next.add(activeItem.id);
                          return next;
                        });
                      }}
                      className="p-0.5 text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0"
                    >
                      {isExpanded ? <ChevronDownIcon className="size-3.5" /> : <ChevronRightIcon className="size-3.5" />}
                    </button>
                  )}
                  <span className="flex-1 text-sm text-gray-200">{activeItem.label}</span>
                  <span className="text-xs text-gray-400">
                    {isSingleTag ? "Topic tag" : sectionLabel}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggle(selectionKey, activeItem.id); }}
                    className="p-0.5 text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    <XMarkIcon className="size-3.5" />
                  </button>
                </div>

                {isExpanded && hasContent && (
                  <div className="pl-10 pr-4 py-1.5 bg-gray-900/30 border-l-2 border-gray-700/30 rounded-b">
                    <div className="flex flex-wrap gap-1">
                      {tickers.map((t, i) => (
                        <span key={i} className="rounded border border-blue-800/40 bg-blue-900/30 px-1.5 py-0.5 text-xs text-blue-200">
                          {t}
                        </span>
                      ))}
                      {keywords.map((k, i) => (
                        <span key={i} className="rounded border border-purple-800/40 bg-purple-900/30 px-1.5 py-0.5 text-xs text-purple-200">
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Create Button ───────────────────────────────────────────────────────

function SectionCreateButton({ action }: { action: { label: string; onClick: () => void } }) {
  return (
    <div className="px-4 py-2">
      <button
        onClick={action.onClick}
        className="flex items-center gap-1.5 text-xs text-squawk-blue hover:text-squawk-blue/80 transition-colors"
      >
        <PlusIcon className="size-3.5" />
        {action.label}
      </button>
    </div>
  );
}

// ── Path resolution ─────────────────────────────────────────────────────

function resolveDisplayGroups(
  activePath: string[],
  sections: LibrarySection[],
  searchQuery: string
): DisplayGroup[] {
  const query = searchQuery.toLowerCase().trim();

  // Search: flat results across all sections
  if (query) {
    return sections
      .map((section) => flattenSectionForSearch(section, query))
      .filter(
        (g) =>
          g.items.length > 0 ||
          g.categories.some((c) => c.items.length > 0)
      );
  }

  // Root level and section level are handled by ContentArea directly
  // This function is only called for group level and deeper
  if (activePath.length < 2) return [];

  const section = sections.find((s) => s.id === activePath[0]);
  if (!section) return [];

  const group = section.groups.find((g) => g.id === activePath[1]);
  if (!group) return [];

  // Group level: show all items, categorized
  if (activePath.length === 2) {
    return [groupToDisplay(section.id, group)];
  }

  // Category level: show only items in that category
  if (activePath.length === 3 && group.categories) {
    const cat = group.categories.find((c) => c.id === activePath[2]);
    if (!cat) return [];
    const catItems = group.items.filter((item) => cat.itemIds.includes(item.id));
    return [{
      sectionId: section.id,
      groupLabel: cat.label,
      categories: [],
      items: catItems,
      createAction: group.createAction,
    }];
  }

  return [];
}

function groupToDisplay(sectionId: string, group: LibraryGroup): DisplayGroup {
  if (!group.categories || group.categories.length === 0) {
    return {
      sectionId,
      groupLabel: group.label,
      categories: [],
      items: group.items,
      createAction: group.createAction,
    };
  }

  const categorizedIds = new Set(group.categories.flatMap((c) => c.itemIds));
  const uncategorized = group.items.filter((item) => !categorizedIds.has(item.id));

  const categories = group.categories
    .map((cat) => ({
      label: cat.label,
      items: group.items.filter((item) => cat.itemIds.includes(item.id)),
    }))
    .filter((c) => c.items.length > 0);

  return {
    sectionId,
    groupLabel: group.label,
    categories,
    items: uncategorized,
    createAction: group.createAction,
  };
}

function flattenSectionForSearch(section: LibrarySection, query: string): DisplayGroup {
  const matchingItems = section.groups.flatMap((group) =>
    group.items.filter((item) => item.label.toLowerCase().includes(query))
  );

  return {
    sectionId: section.id,
    groupLabel: section.label,
    categories: [],
    items: matchingItems,
  };
}
